import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share, Plus, X, Smartphone, ChevronDown } from "lucide-react";

/**
 * iOS Safari install banner.
 *
 * Apple does NOT expose a programmatic "Add to Home Screen" API. The user
 * has to manually tap Share → Aggiungi a Home. This banner walks them
 * through the steps with an animated illustration so the gesture is
 * unmistakable, even for non-tech users.
 *
 * Visible only on:
 *  - iOS devices
 *  - Safari (it's the only iOS browser that supports the Add to Home
 *    Screen flow; in-app webviews like Instagram/Facebook are filtered out)
 *  - Not already in standalone mode
 *  - Not dismissed in the last 30 days
 */
const KEY = "vdh_ios_install_state_v1";
const COOLDOWN_DAYS = 30;

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isSafari() {
  const ua = navigator.userAgent;
  // Filter out in-app webviews: FBAN/FBAV (Facebook), Instagram, Line, etc.
  if (/FBAN|FBAV|Instagram|Line\//i.test(ua)) return false;
  // Chrome on iOS uses CriOS, Firefox uses FxiOS, Edge uses EdgiOS
  if (/CriOS|FxiOS|EdgiOS/i.test(ua)) return false;
  return /Safari/i.test(ua);
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function shouldShow() {
  if (typeof window === "undefined") return false;
  if (!isIOS()) return false;
  if (!isSafari()) return false;
  if (isStandalone()) return false;

  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return true;
    const data = JSON.parse(raw);
    if (data.state === "dismissed" && data.ts) {
      const elapsed = (Date.now() - data.ts) / (1000 * 60 * 60 * 24);
      return elapsed > COOLDOWN_DAYS;
    }
    return true;
  } catch {
    return true;
  }
}

function persist(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ state, ts: Date.now() }));
  } catch {
    /* noop */
  }
}

export default function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(1); // 1: tap share | 2: tap "Aggiungi a Home"

  useEffect(() => {
    if (!shouldShow()) return;
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, []);

  // Auto-cycle the animation: 1 → 2 → 1 every 3.5s
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setStep((s) => (s === 1 ? 2 : 1)), 3500);
    return () => clearInterval(id);
  }, [visible]);

  const dismiss = () => {
    persist("dismissed");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-3 inset-x-3 md:hidden z-[100] vdh-safe-bottom"
        data-testid="ios-install-banner"
      >
        <div
          className="relative rounded-3xl p-4 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(180,125,255,0.4),0_12px_40px_rgba(0,0,0,0.6)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(180,125,255,0.18) 0%, rgba(13,13,15,0.96) 60%)",
            border: "1px solid rgba(180,125,255,0.4)",
          }}
        >
          <button
            type="button"
            onClick={dismiss}
            aria-label="Chiudi"
            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/55"
            data-testid="ios-dismiss"
          >
            <X size={14} />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl flex-none flex items-center justify-center bg-gradient-to-br from-[#b47dff] to-[#ff4fcf] text-black">
              <Smartphone size={22} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-[17px] leading-tight tracking-tight text-white">
                Aggiungi a Home
              </div>
              <div className="text-[11.5px] text-white/65 font-tech">
                2 tap su Safari iOS — in pochi secondi
              </div>
            </div>
          </div>

          {/* Animated step illustration */}
          <div className="rounded-2xl bg-black/40 ring-1 ring-white/8 px-3 py-3 flex items-center gap-3 overflow-hidden">
            <AnimatePresence mode="wait">
              {step === 1 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.35 }}
                  className="flex items-center gap-3 flex-1 min-w-0"
                  data-testid="ios-step-1"
                >
                  <div className="relative w-11 h-11 rounded-xl flex-none flex items-center justify-center bg-[#007aff]/15 ring-1 ring-[#007aff]/40 text-[#5aaaff]">
                    <Share size={20} strokeWidth={1.8} />
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-xl animate-ping opacity-60 ring-2 ring-[#5aaaff]/60"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-tech uppercase tracking-[0.25em] text-[#5aaaff] mb-0.5">
                      Step 1
                    </div>
                    <div className="text-[13px] text-white leading-tight">
                      Tocca l'icona <strong>Condividi</strong> in basso a Safari
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className="text-white/35 flex-none animate-bounce"
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 }}
                  transition={{ duration: 0.35 }}
                  className="flex items-center gap-3 flex-1 min-w-0"
                  data-testid="ios-step-2"
                >
                  <div className="relative w-11 h-11 rounded-xl flex-none flex items-center justify-center bg-[var(--vdh-yellow)]/15 ring-1 ring-[var(--vdh-yellow)]/45 text-[var(--vdh-yellow)]">
                    <Plus size={22} strokeWidth={2.2} />
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-xl animate-ping opacity-60 ring-2 ring-[var(--vdh-yellow)]/60"
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-tech uppercase tracking-[0.25em] text-[var(--vdh-yellow)] mb-0.5">
                      Step 2
                    </div>
                    <div className="text-[13px] text-white leading-tight">
                      Scorri e tocca <strong>"Aggiungi a Home"</strong>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-3">
            {[1, 2].map((n) => (
              <span
                key={n}
                className="h-1 rounded-full transition-all duration-300"
                style={{
                  width: step === n ? 22 : 6,
                  background: step === n ? "#b47dff" : "rgba(255,255,255,0.18)",
                }}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
